import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output } from "@angular/core";

import {
  OnboardingBaseModalComponent,
  OnboardingModalPointerSide,
} from "@common/onboarding-base-modal/onboarding-base-modal.component";

/** Which side of the target the overlay resolved to, so the pointer triangle faces the target. */
export type CoachMarkPointerSide = OnboardingModalPointerSide;

/**
 * FLIP-303. Visual shell for the anchored coach-mark ("Anchored prompt with/without Buttons" in
 * the onboarding Figma file, node-id 20-5582 / 20-6665) - CDK overlay positioning and target
 * resolution live in CoachMarkService, this component only renders the card, via the shared
 * OnboardingBaseModalComponent shell (secondaryStyle="pill" - two solid pills side by side,
 * unlike the Centred modal's pill-then-link footer).
 *
 * The Figma file ships one frame per fixed anchor direction (Top Right, Bottom Centre, ...); this
 * component instead takes the pointer side and offset CoachMarkService actually resolved at
 * runtime - the card is centred on the viewport below or above the target, and the pointer moves
 * along its edge to face the target - and draws the pointer with CSS.
 *
 * "With buttons" vs "without buttons" is the same component: omit primaryLabel for the
 * without-buttons variant.
 */
@Component({
  selector: "app-coach-mark-prompt",
  standalone: true,
  imports: [CommonModule, OnboardingBaseModalComponent],
  templateUrl: "./coach-mark-prompt.component.html",
  styleUrls: ["./coach-mark-prompt.component.scss"],
})
export class CoachMarkPromptComponent {
  @Input() heading = "";
  @Input() message = "";
  /** `undefined` renders no pointer - CoachMarkService's unanchored fallback card. */
  @Input() pointerSide: CoachMarkPointerSide | undefined = "top";
  /** Pointer position along a top/bottom edge, in px from the card's left - see CoachMarkService. */
  @Input() pointerOffset?: number;

  /** Omit both to render the "without buttons" variant. */
  @Input() primaryLabel?: string;
  @Input() secondaryLabel?: string;

  @Output() dismissed = new EventEmitter<void>();
  @Output() primaryAction = new EventEmitter<void>();
  @Output() secondaryAction = new EventEmitter<void>();

  onDismiss(): void {
    this.dismissed.emit();
  }

  onPrimary(): void {
    this.primaryAction.emit();
  }

  onSecondary(): void {
    this.secondaryAction.emit();
  }
}
